import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import resourcesToBackend from "i18next-resources-to-backend";

// RTL languages
export const RTL_LANGUAGES = new Set(["ar", "he"]);

export const SUPPORTED_LANGUAGES = [
  { value: "da", label: "Dansk" },
  { value: "de", label: "Deutsch" },
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "nb", label: "Norsk" },
  { value: "pt", label: "Português" },
  { value: "sv", label: "Svenska" },
] as const;

// Vite dynamic imports — each locale becomes its own JS chunk, loaded on
// demand. Keeps code-splitting savings (~135 KB on first load) without
// relying on /public/locales being routable in every environment.
const localeLoaders: Record<string, () => Promise<{ default: unknown }>> = {
  da: () => import("./locales/da/ui.json"),
  de: () => import("./locales/de/ui.json"),
  en: () => import("./locales/en/ui.json"),
  es: () => import("./locales/es/ui.json"),
  fr: () => import("./locales/fr/ui.json"),
  nb: () => import("./locales/nb/ui.json"),
  pt: () => import("./locales/pt/ui.json"),
  sv: () => import("./locales/sv/ui.json"),
};

// Detect language: explicit user choice (localStorage) wins over browser locale.
const LANG_PREF_KEY = "td_lang_pref";

function detectLanguage(): string {
  const supported = SUPPORTED_LANGUAGES.map((l) => l.value) as readonly string[];
  try {
    const stored = localStorage.getItem(LANG_PREF_KEY);
    if (stored && supported.includes(stored)) return stored;
  } catch {}
  try {
    const browserLang = navigator.language?.toLowerCase() || "";
    const shortCode = browserLang.split("-")[0];
    if (supported.includes(shortCode)) return shortCode;
    if (shortCode === "nn") return "nb";
    if (browserLang.startsWith("pt")) return "pt";
  } catch {}
  return "en";
}

i18n
  .use(
    resourcesToBackend(async (language: string, _namespace: string) => {
      const loader = localeLoaders[language];
      if (!loader) throw new Error(`Unsupported language: ${language}`);
      const mod = await loader();
      return (mod as { default: unknown }).default;
    }),
  )
  .use(initReactI18next)
  .init({
    lng: detectLanguage(),
    fallbackLng: "en",
    defaultNS: "ui",
    ns: ["ui"],
    react: {
      useSuspense: false,
    },
    interpolation: {
      escapeValue: false,
    },
    load: "languageOnly",
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.value),
  });

// Persist every language change so /auth, /onboarding and other unauthenticated
// routes inherit the user's choice across navigation and full reloads.
i18n.on("languageChanged", (lng) => {
  try {
    if (lng) localStorage.setItem(LANG_PREF_KEY, lng);
  } catch {}
});

export default i18n;
